import React, { useState } from "react";
import { Box, Avatar, Typography, Grid, Paper, Card, CardContent, Button, IconButton } from "@mui/material";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import BasicTable from "./Callstable"; // Assuming this is another component you're importing
import CallHistory from "./Callhistory"; // Assuming this is another component you're importing
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const TeamOverview = ({ selectedContact }) => {
    // Define states to track visibility of CallHistory and selected candidate
    const [showCallHistory, setShowCallHistory] = useState(false); // Controls CallHistory visibility
    const [selectedCandidate, setSelectedCandidate] = useState(null); // Selected candidate
    const [callHistoryData, setCallHistoryData] = useState({}); // Object to store call history for each candidate

    // If no recruiter is selected, display a loading message or prompt
    if (!selectedContact) {
        return <Typography>Select a recruiter to view details</Typography>;
    }

    // Handle View button click from BasicTable to set selected candidate and show CallHistory
    const handleViewClick = (candidate) => {
        setSelectedCandidate(candidate);
        setShowCallHistory(true); // Show CallHistory when view is clicked

        // Retrieve the candidate's call history, either from state or simulate fetching
        if (!callHistoryData[candidate.id]) {
            // Simulate fetching call history for the candidate
            setCallHistoryData(prevState => ({
                ...prevState,
                [candidate.id]: [
                    { date: "2025-02-01", status: "Completed", notes: "First call with candidate." },
                    { date: "2025-02-05", status: "In Progress", notes: "Follow-up call." }
                ]
            }));
        }
    };

    // Handle Back button click inside CallHistory to hide it and show the table
    const handleBackClick = () => {
        setShowCallHistory(false); // Show table and hide CallHistory
        setSelectedCandidate(null); // Optionally reset selected candidate
    };

    const statusData = [
        { name: "New", value: 3, color: "#D89C00" },
        { name: "In Progress", value: 0, color: "#D81BC5" },
        { name: "Contacted", value: 0, color: "#5859E2" },
        { name: "Closed", value: 0, color: "#18A558" },
    ];

    const pastelColors = ["#FFD1DC", "#FFECB3", "#C8E6C9", "#BBDEFB", "#D1C4E9", "#FFCCBC"];

    const getAvatarColor = (name, index) => {
        if (!name) return pastelColors[index % pastelColors.length];
        const charCode = name.charCodeAt(0);
        return pastelColors[charCode % pastelColors.length];
    };

    return (
        <Box p={1} sx={{ bgcolor: "grey.100", minHeight: "100vh" }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" bgcolor="white" p={1} borderRadius={2} boxShadow={2}>
                <Box display="flex" alignItems="center" gap={2}>
                    <Avatar sx={{
                        bgcolor: getAvatarColor(selectedContact.recruiter_name, 0),
                        width: 42,
                        height: 42,
                        fontSize: "1rem",
                        color: "#333" // Dark text for contrast
                    }}>
                        {selectedContact.recruiter_name.charAt(0).toUpperCase()} {/* Display first letter */}
                    </Avatar>
                    <Box>
                        <Typography variant="h6" fontWeight="bold">{selectedContact.recruiter_name}</Typography>
                        <Typography color="textSecondary">Phone: {selectedContact.phone}</Typography>
                        <Typography color="primary">Email: {selectedContact.recruiter_email}</Typography>
                        <Typography color="textSecondary">Role: {selectedContact.role}</Typography>
                    </Box>
                </Box>
            </Box>

            <Grid container spacing={4} alignItems="center" mt={0}>
                {/* Right Side - Status Cards (2x2 Grid) */}
                <Grid container spacing={2} item xs={12} md={7}>
                    {statusData?.map((item) => (
                        <Grid item xs={12} md={6} key={item.name}>
                            <Card sx={{ bgcolor: item.color, color: "white", boxShadow: 3, borderRadius: 2, height: "120px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                <CardContent sx={{ textAlign: "center", padding: 2 }}>
                                    <Typography variant="h6" fontWeight="">{item.name}</Typography>
                                    <Typography variant="h5" fontWeight="">{item.value}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Left Side - Pie Chart */}
                <Grid item xs={12} md={5}>
                    <Paper sx={{ p: 1, boxShadow: 2, borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight="bold" mb={2}>Status Overview</Typography>
                        <PieChart width={400} height={250}>
                            <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}>
                                {statusData?.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color} />
                                ))}
                            </Pie>
                            <Tooltip />
                            <Legend />
                        </PieChart>
                    </Paper>
                </Grid>

                {/* Table or CallHistory based on showCallHistory state */}
                <Grid item xs={12}>
                    <Paper>
                        {showCallHistory ? (
                            <>
                                {/* Back Arrow */}
                                <Box display="flex" alignItems="center" mb={2}>
                                    <IconButton onClick={handleBackClick} color="primary">
                                        <ArrowBackIcon fontSize="small" />
                                    </IconButton>
                                    <Typography variant="h6" fontWeight="bold" ml={1} sx={{fontSize:"15px"}}>
                                        Call History
                                    </Typography>
                                </Box>

                                {/* Show CallHistory Component */}
                                {selectedCandidate && <CallHistory candidate={selectedCandidate}  />}
                            </>
                        ) : (
                            <>
                                {/* Show Basic Table Component */}
                                <BasicTable onViewClick={handleViewClick} />
                            </>
                        )}
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default TeamOverview;
